import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import NotificationBell from '@/components/NotificationBell';
import AssistantsPicker from '@/components/staff/AssistantsPicker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { FaultPhotoPicker, uploadFaultPhotos } from '@/components/fleet/FaultPhotos';
import {
  MapPin, LogIn, LogOut, ClipboardCheck, Building2, Truck, Wrench, ShieldAlert, ArrowRightLeft, CircleDot, PlusCircle, X, Camera, TrendingUp, Wallet, HandCoins, FileText, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { key: 'on_duty', label: 'On Duty', icon: LogIn, color: 'text-emerald-600' },
  { key: 'attendance', label: 'Attendance Register', icon: ClipboardCheck, color: 'text-indigo-600' },
  { key: 'depot_arrival', label: 'Depot Arrival', icon: Building2, color: 'text-sky-600' },
  { key: 'depot_departure', label: 'Depot Departure', icon: ArrowRightLeft, color: 'text-blue-600' },
  { key: 'vehicle_inspection', label: 'Vehicle Inspection', icon: ShieldAlert, color: 'text-amber-600' },
  { key: 'fault_reporting', label: 'Fault Reporting', icon: Wrench, color: 'text-orange-600' },
  { key: 'workshop_job_cards', label: 'Workshop Job Cards', icon: Truck, color: 'text-purple-600' },
  { key: 'return_to_depot', label: 'Return to Depot', icon: Building2, color: 'text-cyan-600' },
  { key: 'off_duty', label: 'Off Duty', icon: LogOut, color: 'text-rose-600' },
];

const INSPECTION_CHECKS = [
  'Tyres',
  'Lights & indicators',
  'Brakes',
  'Fluid levels (oil, water, brake)',
  'Wipers & mirrors',
  'Fuel level',
  'Interior clean',
  'License disc valid',
];

// Wrap browser geolocation in a promise with a timeout
function getGPS() {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 30_000 },
    );
  });
}

export default function DriverShift() {
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicleReg, setVehicleReg] = useState('');
  const [gpsHint, setGpsHint] = useState('');

  // Per-step scratch state — reset each time the driver moves on
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [depotArrivalKm, setDepotArrivalKm] = useState('');
  const [depotDepartureKm, setDepotDepartureKm] = useState('');
  const [inspectionChecks, setInspectionChecks] = useState({});
  const [inspectionOk, setInspectionOk] = useState(true);
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [faults, setFaults] = useState([]);
  const [jobCards, setJobCards] = useState('');
  const [returnKm, setReturnKm] = useState('');
  const [offDutyNotes, setOffDutyNotes] = useState('');
  const [startKm, setStartKm] = useState('');
  const [endKm, setEndKm] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [summary, setSummary] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/driver-shifts/current`, { headers });
      setShift(res.data || null);
    } catch (err) {
      toast.error('Could not load shift');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  // Load YTD earnings + loans summary (background)
  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/driver-shifts/my-earnings-summary`, { headers });
      setSummary(r.data);
    } catch { setSummary(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Keep photos state in sync with the shift record
  useEffect(() => {
    const p = shift?.steps?.vehicle_inspection?.photos || [];
    setPhotos(p);
  }, [shift]);

  const uploadPhoto = async (file) => {
    if (!file || !shift) return;
    if (!file.type?.startsWith('image/')) return toast.error('Only images allowed');
    if (file.size > 8 * 1024 * 1024) return toast.error('Image must be under 8MB');
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/driver-shifts/${shift.id}/inspection-photo`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setPhotos([...photos, res.data]);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setPhotoUploading(false); }
  };

  const R = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const downloadStatement = async (s) => {
    try {
      const res = await axios.get(`${API}/statements/${s.id}/download`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = s.file_name || `payslip-${s.id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const startShift = async () => {
    if (!vehicleReg.trim()) return toast.error('Enter the vehicle registration first');
    const km = Number(startKm);
    if (!km || km <= 0) return toast.error('Enter the current odometer reading (start KM)');
    setSaving(true);
    setGpsHint('Getting GPS…');
    try {
      const gps = await getGPS();
      setGpsHint(gps ? `GPS captured (accuracy ${Math.round(gps.accuracy)}m)` : 'GPS unavailable — timestamp only');
      const body = {
        vehicle_registration: vehicleReg.trim().toUpperCase(),
        start_km: km,
        ...(gps || {}),
      };
      const res = await axios.post(`${API}/driver-shifts/start`, body, { headers });
      setShift(res.data);
      setStartKm('');
      toast.success('On duty — clock started');
      if (res.data.odometer_mismatch) {
        toast.warning(`Your start km is ${res.data.odometer_mismatch.difference_km > 0 ? '+' : ''}${res.data.odometer_mismatch.difference_km} km different from the last reading (${res.data.odometer_mismatch.previous_km} km). Admin has been asked to check it.`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start shift');
    } finally { setSaving(false); }
  };

  const submitStep = async (step, data, opts = {}) => {
    setSaving(true);
    try {
      const payload = { step, data };
      const res = await axios.post(`${API}/driver-shifts/${shift.id}/step`, payload, { headers });
      setShift(res.data);
      toast.success(opts.successMessage || 'Saved');
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const submitWithGPS = async (step, extra = {}, opts = {}) => {
    setGpsHint('Getting GPS…');
    const gps = await getGPS();
    setGpsHint(gps ? `GPS ${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}` : 'GPS unavailable');
    await submitStep(step, { ...(gps || {}), ...extra }, opts);
  };

  const stepDone = (key) => shift?.steps && shift.steps[key];
  const currentStepKey = shift?.current_step;

  const stepBanner = (
    <div className="grid grid-cols-3 sm:grid-cols-9 gap-1 mb-4" data-testid="shift-stepper">
      {STEPS.map((s, i) => {
        const done = stepDone(s.key);
        const active = currentStepKey === s.key;
        const Icon = s.icon;
        return (
          <div
            key={s.key}
            className={`flex flex-col items-center text-center rounded-md p-2 border ${
              done ? 'bg-emerald-50 border-emerald-300' :
              active ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-400' :
              'bg-slate-50 border-slate-200'
            }`}
          >
            <Icon className={`h-4 w-4 ${done ? 'text-emerald-600' : active ? 'text-amber-600' : 'text-slate-400'}`} />
            <span className="text-[10px] mt-1 leading-tight">{i + 1}. {s.label}</span>
          </div>
        );
      })}
    </div>
  );

  const renderCurrentStepForm = () => {
    if (!shift) return null;
    const key = shift.current_step;
    switch (key) {
      case 'attendance':
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Confirm you're clocked in for the day. Add any note (e.g. late reason).
            </p>
            <Textarea placeholder="Notes (optional)" value={attendanceNotes} onChange={(e) => setAttendanceNotes(e.target.value)} data-testid="attendance-notes" />
            <Button onClick={() => submitStep('attendance', { notes: attendanceNotes }, { successMessage: 'Attendance recorded' })} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="attendance-submit">
              <ClipboardCheck className="h-4 w-4 mr-1" /> Confirm Attendance
            </Button>
          </div>
        );
      case 'depot_arrival':
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Mark that you've arrived at the depot. GPS is captured automatically.</p>
            <Button onClick={() => submitWithGPS('depot_arrival', {}, { successMessage: 'Depot arrival logged' })} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="depot-arrival-submit">
              <MapPin className="h-4 w-4 mr-1" /> Log Depot Arrival
            </Button>
          </div>
        );
      case 'depot_departure':
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Leaving the depot to start deliveries. Enter the odometer (km) reading.</p>
            <Input type="number" placeholder="Odometer km" value={depotDepartureKm} onChange={(e) => setDepotDepartureKm(e.target.value)} data-testid="depot-departure-km" />
            <Button onClick={() => submitWithGPS('depot_departure', { odometer_km: Number(depotDepartureKm) || 0 }, { successMessage: 'Departure logged' })} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="depot-departure-submit">
              <MapPin className="h-4 w-4 mr-1" /> Depart Depot
            </Button>
          </div>
        );
      case 'vehicle_inspection': {
        const toggle = (label) => setInspectionChecks((prev) => ({ ...prev, [label]: !prev[label] }));
        const API_BASE = process.env.REACT_APP_BACKEND_URL;
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Tick every item you verified is in working order.</p>
            <div className="grid grid-cols-2 gap-2">
              {INSPECTION_CHECKS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm p-2 border rounded bg-white cursor-pointer">
                  <input type="checkbox" checked={!!inspectionChecks[c]} onChange={() => toggle(c)} data-testid={`inspect-${c.split(' ')[0].toLowerCase()}`} />
                  {c}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={inspectionOk} onChange={(e) => setInspectionOk(e.target.checked)} data-testid="inspect-overall-ok" />
              <span>Overall vehicle roadworthy</span>
            </label>

            {/* Photo uploads */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium flex items-center gap-1">
                  <Camera className="h-4 w-4 text-amber-600" /> Photos ({photos.length})
                </span>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                    data-testid="inspect-photo-input"
                  />
                  <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border bg-white hover:bg-amber-50 border-amber-300 text-amber-700">
                    <Camera className="h-3 w-3" />
                    {photoUploading ? 'Uploading…' : 'Snap Photo'}
                  </span>
                </label>
              </div>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((p) => (
                    <a
                      key={p.id}
                      href={`${API_BASE}${p.url}`}
                      target="_blank" rel="noreferrer"
                      className="block border rounded overflow-hidden bg-white hover:ring-2 hover:ring-amber-400"
                      data-testid={`inspect-photo-${p.id}`}
                    >
                      <img src={`${API_BASE}${p.url}`} alt={p.filename} className="w-full h-24 object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">Tyres, dashboard, damage — anything admin should see.</p>
            </div>

            <Textarea placeholder="Inspection notes (optional)" value={inspectionNotes} onChange={(e) => setInspectionNotes(e.target.value)} data-testid="inspect-notes" />
            <Button onClick={() => submitStep('vehicle_inspection', { checks: inspectionChecks, overall_ok: inspectionOk, notes: inspectionNotes, photo_count: photos.length }, { successMessage: 'Inspection saved' })} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="inspect-submit">
              <ShieldAlert className="h-4 w-4 mr-1" /> Submit Inspection
            </Button>
          </div>
        );
      }
      case 'fault_reporting': {
        const addFault = () => setFaults([...faults, { description: '', severity: 'medium' }]);
        const setFault = (i, patch) => setFaults(faults.map((f, idx) => idx === i ? { ...f, ...patch } : f));
        const remove = (i) => setFaults(faults.filter((_, idx) => idx !== i));
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Log any vehicle faults now. Each one will be sent to Fleet Maintenance as a fault ticket for admin.
              Nothing to report? Just click <b>No Faults</b>.
            </p>
            {faults.map((f, i) => (
              <div key={i} className="border rounded p-2 space-y-2 bg-white">
                <Textarea placeholder={`Fault ${i + 1} description`} value={f.description} onChange={(e) => setFault(i, { description: e.target.value })} data-testid={`fault-desc-${i}`} />
                <FaultPhotoPicker files={f.photos || []} onChange={(photos) => setFault(i, { photos })} testId={`fault-photo-${i}`} />
                <div className="flex items-center gap-2">
                  <select value={f.severity} onChange={(e) => setFault(i, { severity: e.target.value })} className="h-9 rounded border px-2 text-sm" data-testid={`fault-sev-${i}`}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-rose-600" data-testid={`fault-remove-${i}`}><X className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="outline" onClick={addFault} data-testid="fault-add"><PlusCircle className="h-4 w-4 mr-1" /> Add Fault</Button>
              <Button onClick={async () => {
                const valid = faults.filter((x) => (x.description || '').trim());
                const res = await submitStep('fault_reporting', { faults: valid.map(({ photos, ...x }) => x) }, { successMessage: valid.length > 0 ? `${valid.length} fault(s) reported` : 'No faults reported' });
                const ids = res?.steps?.fault_reporting?.fault_ids || [];
                if (ids.length && valid.some((x) => (x.photos || []).length)) {
                  const r = await uploadFaultPhotos(ids, valid, headers);
                  if (r.ok) toast.success(`${r.ok} photo(s) attached`);
                  if (r.failed) toast.error(`${r.failed} photo(s) failed to upload`);
                }
              }} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="fault-submit">
                {faults.length > 0 ? 'Submit Faults' : 'No Faults'}
              </Button>
            </div>
          </div>
        );
      }
      case 'workshop_job_cards':
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              If the vehicle was taken to the workshop today, note any existing job-card / ticket numbers here (comma-separated). Otherwise leave blank.
            </p>
            <Input placeholder="Job Card # (comma-separated, or leave blank)" value={jobCards} onChange={(e) => setJobCards(e.target.value)} data-testid="job-cards-input" />
            <Button onClick={() => submitStep('workshop_job_cards', { job_card_refs: jobCards.split(',').map(s => s.trim()).filter(Boolean) }, { successMessage: 'Workshop notes saved' })} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="job-cards-submit">
              <Wrench className="h-4 w-4 mr-1" /> Submit
            </Button>
          </div>
        );
      case 'return_to_depot':
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Deliveries done — back at the depot. Enter the current odometer (km) reading.</p>
            <Input type="number" placeholder="Odometer km" value={returnKm} onChange={(e) => setReturnKm(e.target.value)} data-testid="return-km" />
            <Button onClick={() => submitWithGPS('return_to_depot', { odometer_km: Number(returnKm) || 0 }, { successMessage: 'Return logged' })} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="return-submit">
              <MapPin className="h-4 w-4 mr-1" /> Log Return
            </Button>
          </div>
        );
      case 'off_duty':
        {
          const startKmVal = shift?.start_km || 0;
          const endKmNum = Number(endKm) || 0;
          const distance = endKmNum && startKmVal && endKmNum >= startKmVal ? endKmNum - startKmVal : 0;
          const valid = endKmNum > 0 && (!startKmVal || endKmNum >= startKmVal);
          return (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Ending your shift. Enter the closing odometer (km) — required, and any final notes for admin.</p>
              {startKmVal > 0 && (
                <div className="text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded px-2 py-1">
                  Start KM: <b>{startKmVal.toLocaleString('en-ZA')}</b>
                </div>
              )}
              <Input
                type="number"
                inputMode="numeric"
                min={startKmVal || 1}
                placeholder="Closing odometer (km) — required"
                value={endKm}
                onChange={(e) => setEndKm(e.target.value)}
                data-testid="off-duty-end-km"
                className="text-lg"
              />
              {distance > 0 && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  Distance today: <b>{distance.toLocaleString('en-ZA')} km</b>
                </div>
              )}
              {endKmNum > 0 && !valid && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                  End reading can't be less than start ({startKmVal.toLocaleString('en-ZA')} km).
                </div>
              )}
              <Textarea placeholder="End-of-shift notes (optional)" value={offDutyNotes} onChange={(e) => setOffDutyNotes(e.target.value)} data-testid="off-duty-notes" />
              <Button onClick={() => submitWithGPS('off_duty', { notes: offDutyNotes, end_km: endKmNum }, { successMessage: 'Shift ended · Have a good rest' })} disabled={saving || !valid} className="bg-rose-600 hover:bg-rose-700" data-testid="off-duty-submit">
                <LogOut className="h-4 w-4 mr-1" /> Clock Off Duty
              </Button>
            </div>
          );
        }
      default:
        return null;
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-6 text-center text-muted-foreground">Loading shift…</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="driver-shift-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">My Shift</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Work through each step so admin can see your day. Everything you save is sent to the office immediately.
            </p>
          </div>
          <NotificationBell onNavigate={(url) => { if (url === '/driver/shift') loadSummary(); }} />
        </div>

        {/* YTD Earnings + Payslips */}
        {summary && (
          <Card data-testid="earnings-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                My Earnings · {summary.year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                  <div className="text-[10px] uppercase text-emerald-700 flex items-center gap-1"><Wallet className="h-3 w-3" /> Gross Paid YTD</div>
                  <div className="text-lg font-extrabold text-emerald-700" data-testid="gross-ytd">{R(summary.gross_paid_ytd)}</div>
                  <div className="text-[10px] text-muted-foreground">{summary.statements_count} payslip(s) · All-time {R(summary.gross_paid_all_time)}</div>
                </div>
                <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3 sm:col-span-2">
                  <div className="text-[10px] uppercase text-indigo-700">Loan Ledger · {summary.loans_paid_out_count ?? 0} paid out</div>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <div>
                      <div className="text-[9px] text-slate-600">Disbursed YTD</div>
                      <div className="text-sm font-bold text-slate-800" data-testid="loans-disbursed-ytd">{R(summary.loans_disbursed_ytd)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-emerald-700">Repaid YTD</div>
                      <div className="text-sm font-bold text-emerald-700" data-testid="loans-repaid-ytd">{R(summary.loans_repaid_ytd)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-rose-700">Still Owed</div>
                      <div className="text-sm font-extrabold text-rose-700" data-testid="loans-outstanding">{R(summary.loans_outstanding)}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Net YTD (Gross − Disbursed): <b className="text-indigo-700" data-testid="net-ytd">{R(summary.net_ytd)}</b>
                  </div>
                </div>
              </div>
              {/* Recent payslips w/ download */}
              {summary.statements.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-medium mb-1">Recent payslips</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {summary.statements.slice(0, 8).map(s => (
                      <div key={s.id} className="flex items-center justify-between text-xs border rounded px-2 py-1 bg-white" data-testid={`payslip-row-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{s.file_name || 'Payslip'}</div>
                          <div className="text-[10px] text-muted-foreground">{s.period_start || '—'} → {s.period_end || '—'} · {R(s.amount)}</div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => downloadStatement(s)} data-testid={`download-payslip-${s.id}`}>
                          <Download className="h-3 w-3 mr-1" /> Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {summary.statements.length === 0 && (
                <p className="text-xs text-muted-foreground italic mt-1">No payslips issued to you yet.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vehicle change requests + Assistants (always visible for drivers) */}
        <AssistantsPicker testIdPrefix="driver" />

        {!shift ? (
          /* -------- START SHIFT PANEL -------- */
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <LogIn className="h-5 w-5 text-emerald-600" />
                Clock On Duty
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Enter your vehicle registration and the current odometer reading, then tap the button below. Your GPS position and time will be captured and sent to admin. The odometer flows through to Fleet Maintenance so service scheduling stays up to date.
              </p>
              <Input placeholder="Vehicle registration (e.g. KY88LGP)" value={vehicleReg} onChange={(e) => setVehicleReg(e.target.value.toUpperCase())} data-testid="shift-vehicle-reg" className="text-lg" />
              <Input
                type="number"
                inputMode="numeric"
                min="1"
                placeholder="Starting odometer (km) — required"
                value={startKm}
                onChange={(e) => setStartKm(e.target.value)}
                data-testid="shift-start-km"
                className="text-lg"
              />
              <Button size="lg" onClick={startShift} disabled={saving || !vehicleReg.trim() || !startKm} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="start-shift-btn">
                <LogIn className="h-5 w-5 mr-2" /> On Duty — Start Shift
              </Button>
              {gpsHint && <p className="text-xs text-muted-foreground text-center">{gpsHint}</p>}
            </CardContent>
          </Card>
        ) : (
          /* -------- ACTIVE SHIFT PANEL -------- */
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CircleDot className="h-4 w-4 text-emerald-600 animate-pulse" />
                    Active Shift · {shift.vehicle_registration || '—'}
                  </CardTitle>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Started {new Date(shift.started_at).toLocaleTimeString()} · {shift.shift_date}
                  </p>
                </div>
                <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                  Step: {STEPS.find(s => s.key === shift.current_step)?.label || 'Ready'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {stepBanner}
              <div className="rounded-lg border bg-slate-50 p-4">
                {renderCurrentStepForm()}
                {gpsHint && <p className="text-[10px] text-muted-foreground mt-2">{gpsHint}</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed steps summary */}
        {shift && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Completed today</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {STEPS.filter(s => stepDone(s.key)).map(s => {
                const d = shift.steps[s.key];
                const t = d.ts ? new Date(d.ts).toLocaleTimeString() : '';
                const Icon = s.icon;
                return (
                  <div key={s.key} className="flex items-center gap-2 text-xs">
                    <Icon className={`h-3.5 w-3.5 ${s.color}`} />
                    <span className="font-medium">{s.label}</span>
                    <span className="text-muted-foreground">— {t}</span>
                    {d.lat != null && <span className="text-muted-foreground">· {d.lat.toFixed(4)}, {d.lng.toFixed(4)}</span>}
                  </div>
                );
              })}
              {STEPS.filter(s => stepDone(s.key)).length === 0 && (
                <p className="text-xs text-muted-foreground">Nothing yet — start with On Duty.</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
